import { useState, useEffect, useRef } from "react";
import API, { formatApiError } from "../api/client";
import TopBar from "../components/layout/TopBar";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Building, Upload, Lock, Tag, Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const { user } = useAuth();
  const [companyName, setCompanyName] = useState("");
  const [logoPreview, setLogoPreview] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const fileRef = useRef(null);
  const [flags, setFlags] = useState([]);
  const [newFlagName, setNewFlagName] = useState("");
  const [newFlagColor, setNewFlagColor] = useState("#4353FF");
  const [editingFlag, setEditingFlag] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");

  useEffect(() => {
    API.get("/settings/company").then(r => {
      setCompanyName(r.data.name || "");
      if (r.data.logo_path) {
        API.get("/settings/company/logo-file", { responseType: "blob" })
          .then(r => setLogoPreview(URL.createObjectURL(r.data)))
          .catch(() => {});
      }
    }).catch(() => {});
    loadFlags();
  }, []);

  const loadFlags = () => API.get("/settings/flags").then(r => setFlags(r.data)).catch(() => {});

  if (user?.role !== "admin") {
    return (
      <>
        <TopBar title="Settings" />
        <div className="flex-1 flex items-center justify-center text-[#686B74]">Admin access required</div>
      </>
    );
  }

  const saveCompanyName = async () => {
    try {
      await API.put("/settings/company", { name: companyName });
      toast.success("Company name updated");
    } catch { toast.error("Failed to update"); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await API.post("/settings/company/logo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo uploaded");
      API.get("/settings/company/logo-file", { responseType: "blob" })
        .then(r => setLogoPreview(URL.createObjectURL(r.data)))
        .catch(() => {});
    } catch { toast.error("Upload failed"); }
  };

  const changePassword = async () => {
    if (!currentPassword || !newPassword) return;
    try {
      await API.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed");
      setCurrentPassword("");
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const createFlag = async () => {
    if (!newFlagName.trim()) return;
    try {
      await API.post("/settings/flags", { name: newFlagName, color: newFlagColor });
      toast.success("Flag created");
      setNewFlagName("");
      setNewFlagColor("#4353FF");
      loadFlags();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const updateFlag = async (flagId) => {
    try {
      await API.put(`/settings/flags/${flagId}`, { name: editName, color: editColor });
      toast.success("Flag updated");
      setEditingFlag(null);
      loadFlags();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteFlag = async (flagId) => {
    try {
      await API.delete(`/settings/flags/${flagId}`);
      toast.success("Flag deleted");
      loadFlags();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const startEdit = (flag) => {
    setEditingFlag(flag.id);
    setEditName(flag.name);
    setEditColor(flag.color);
  };

  return (
    <>
      <TopBar title="Settings" />
      <div className="flex-1 overflow-y-auto p-6" data-testid="settings-page">
        <div className="max-w-xl space-y-8">
          {/* Company Profile */}
          <div className="bg-white border border-[#EBEBEB] rounded-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Building className="w-4 h-4 text-[#686B74]" />
              <h2 className="text-sm font-semibold text-[#111216]">Company Profile</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-[#686B74] mb-1.5 block">Company Name</label>
                <div className="flex gap-2">
                  <Input
                    data-testid="company-name-input"
                    value={companyName}
                    onChange={e => setCompanyName(e.target.value)}
                    placeholder="Your company name"
                    className="flex-1"
                  />
                  <Button data-testid="save-company-name-btn" onClick={saveCompanyName} className="bg-[#111216] hover:bg-[#2B2D31] text-white">
                    Save
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#686B74] mb-1.5 block">Company Logo</label>
                <div className="flex items-center gap-4">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo" className="w-16 h-16 rounded-md object-cover border border-[#EBEBEB]" />
                  ) : (
                    <div className="w-16 h-16 rounded-md bg-[#F4F5F8] border border-[#EBEBEB] flex items-center justify-center">
                      <Building className="w-6 h-6 text-[#686B74]" />
                    </div>
                  )}
                  <div>
                    <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" />
                    <Button
                      data-testid="upload-logo-btn"
                      variant="outline"
                      size="sm"
                      onClick={() => fileRef.current?.click()}
                    >
                      <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload Logo
                    </Button>
                    <p className="text-[11px] text-[#686B74] mt-1">PNG, JPG up to 2MB</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Task Flags */}
          <div className="bg-white border border-[#EBEBEB] rounded-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Tag className="w-4 h-4 text-[#686B74]" />
              <h2 className="text-sm font-semibold text-[#111216]">Task Flags</h2>
            </div>
            <p className="text-xs text-[#686B74] mb-4">Create flags to categorise tasks by workflow stage (e.g. Quote Requested, Quoted, Approved). Each task can have one active flag.</p>

            {/* Create new flag */}
            <div className="flex items-center gap-2 mb-4">
              <input
                type="color"
                data-testid="new-flag-color"
                value={newFlagColor}
                onChange={e => setNewFlagColor(e.target.value)}
                className="w-8 h-8 rounded cursor-pointer border border-[#EBEBEB] p-0.5"
                title="Pick flag colour"
              />
              <Input
                data-testid="new-flag-name"
                value={newFlagName}
                onChange={e => setNewFlagName(e.target.value)}
                placeholder="Flag name (e.g. Quote Requested)"
                className="flex-1 h-8 text-sm"
                onKeyDown={e => { if (e.key === "Enter") createFlag(); }}
              />
              <Button data-testid="create-flag-btn" onClick={createFlag} size="sm" className="bg-[#111216] hover:bg-[#2B2D31] text-white h-8">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>

            {/* Flag list */}
            <div className="space-y-1.5">
              {flags.length === 0 && (
                <p className="text-xs text-[#686B74] text-center py-4">No flags created yet</p>
              )}
              {flags.map((flag) => (
                <div key={flag.id} data-testid={`flag-row-${flag.id}`} className="flex items-center gap-2 px-3 py-2 rounded-md border border-[#EBEBEB] hover:bg-[#F9F9FB] transition-colors">
                  {editingFlag === flag.id ? (
                    <>
                      <input
                        type="color"
                        value={editColor}
                        onChange={e => setEditColor(e.target.value)}
                        className="w-7 h-7 rounded cursor-pointer border border-[#EBEBEB] p-0.5"
                      />
                      <Input
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        className="flex-1 h-7 text-sm"
                        autoFocus
                        onKeyDown={e => { if (e.key === "Enter") updateFlag(flag.id); if (e.key === "Escape") setEditingFlag(null); }}
                      />
                      <button onClick={() => updateFlag(flag.id)} className="p-1 text-[#34B56F] hover:text-green-700" data-testid={`save-flag-${flag.id}`}>
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setEditingFlag(null)} className="p-1 text-[#686B74] hover:text-[#111216]">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="w-5 h-5 rounded-full flex-shrink-0 border border-black/10" style={{ backgroundColor: flag.color }} />
                      <span
                        className="text-xs font-medium px-2 py-0.5 rounded-full border flex-shrink-0"
                        style={{ backgroundColor: flag.color + "20", color: flag.color, borderColor: flag.color + "40" }}
                      >
                        {flag.name}
                      </span>
                      <div className="flex-1" />
                      <button onClick={() => startEdit(flag)} className="p-1 text-[#686B74] hover:text-[#111216] transition-colors" data-testid={`edit-flag-${flag.id}`}>
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button onClick={() => deleteFlag(flag.id)} className="p-1 text-[#686B74] hover:text-[#E24A4A] transition-colors" data-testid={`delete-flag-${flag.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Change Password */}
          <div className="bg-white border border-[#EBEBEB] rounded-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-4 h-4 text-[#686B74]" />
              <h2 className="text-sm font-semibold text-[#111216]">Change Password</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-[#686B74] mb-1 block">Current Password</label>
                <Input
                  data-testid="current-password-input"
                  type="password"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#686B74] mb-1 block">New Password</label>
                <Input
                  data-testid="new-password-input"
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters"
                />
              </div>
              <Button data-testid="change-password-btn" onClick={changePassword} className="bg-[#111216] hover:bg-[#2B2D31] text-white">
                Update Password
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
